import { supabase } from '../lib/supabase';

export const attendanceIntegrationService = {
  // ============================================================================
  // ATTENDANCE SYSTEM CONFIGURATION
  // ============================================================================

  async getAllAttendanceSystems() {
    const { data, error } = await supabase
      ?.from('attendance_system_configs')
      ?.select('*')
      ?.order('system_name', { ascending: true });
    
    if (error) throw error;
    
    return data?.map(row => ({
      id: row?.id,
      systemName: row?.system_name,
      systemVendor: row?.system_vendor,
      biometricTypes: row?.biometric_types,
      integrationMethod: row?.integration_method,
      apiEndpoint: row?.api_endpoint,
      syncFrequency: row?.sync_frequency,
      lastSyncAt: row?.last_sync_at,
      nextSyncAt: row?.next_sync_at,
      isActive: row?.is_active,
      createdAt: row?.created_at
    })) || [];
  },

  async getAttendanceSystemById(systemId) {
    const { data, error } = await supabase
      ?.from('attendance_system_configs')
      ?.select('*')
      ?.eq('id', systemId)
      ?.single();
    
    if (error) throw error;
    
    return {
      id: data?.id,
      systemName: data?.system_name,
      systemVendor: data?.system_vendor,
      biometricTypes: data?.biometric_types,
      integrationMethod: data?.integration_method,
      apiEndpoint: data?.api_endpoint,
      apiVersion: data?.api_version,
      syncFrequency: data?.sync_frequency,
      lastSyncAt: data?.last_sync_at,
      isActive: data?.is_active,
      configurationMetadata: data?.configuration_metadata
    };
  },

  async createAttendanceSystem(system) {
    const { data, error } = await supabase
      ?.from('attendance_system_configs')
      ?.insert({
        system_name: system?.systemName,
        system_vendor: system?.systemVendor,
        biometric_types: system?.biometricTypes,
        integration_method: system?.integrationMethod,
        api_endpoint: system?.apiEndpoint,
        api_key_encrypted: system?.apiKeyEncrypted,
        sync_frequency: system?.syncFrequency,
        is_active: system?.isActive !== undefined ? system?.isActive : true,
        configuration_metadata: system?.configurationMetadata,
        created_by: system?.createdBy
      })
      ?.select()
      ?.single();
    
    if (error) throw error;
    
    return {
      id: data?.id,
      systemName: data?.system_name,
      isActive: data?.is_active
    };
  },

  async updateAttendanceSystem(systemId, updates) {
    const updateData = {
      updated_at: new Date()?.toISOString()
    };

    if (updates?.apiEndpoint) updateData.api_endpoint = updates?.apiEndpoint;
    if (updates?.apiKeyEncrypted) updateData.api_key_encrypted = updates?.apiKeyEncrypted;
    if (updates?.syncFrequency) updateData.sync_frequency = updates?.syncFrequency;
    if (updates?.lastSyncAt) updateData.last_sync_at = updates?.lastSyncAt;
    if (updates?.nextSyncAt) updateData.next_sync_at = updates?.nextSyncAt;
    if (updates?.isActive !== undefined) updateData.is_active = updates?.isActive;

    const { data, error } = await supabase
      ?.from('attendance_system_configs')
      ?.update(updateData)
      ?.eq('id', systemId)
      ?.select()
      ?.single();
    
    if (error) throw error;
    
    return {
      id: data?.id,
      isActive: data?.is_active,
      updatedAt: data?.updated_at
    };
  },

  // ============================================================================
  // BIOMETRIC REGISTRATIONS
  // ============================================================================

  async registerEmployeeBiometric(registration) {
    const { data, error } = await supabase
      ?.from('employee_biometric_registrations')
      ?.insert({
        employee_id: registration?.employeeId,
        attendance_system_id: registration?.attendanceSystemId,
        biometric_type: registration?.biometricType,
        biometric_id: registration?.biometricId,
        registration_date: registration?.registrationDate || new Date()?.toISOString()?.split('T')?.[0],
        is_active: registration?.isActive !== undefined ? registration?.isActive : true,
        metadata: registration?.metadata
      })
      ?.select()
      ?.single();
    
    if (error) throw error;
    
    return {
      id: data?.id,
      employeeId: data?.employee_id,
      biometricType: data?.biometric_type,
      biometricId: data?.biometric_id,
      isActive: data?.is_active
    };
  },

  async getEmployeeBiometricRegistrations(employeeId) {
    const { data, error } = await supabase
      ?.from('employee_biometric_registrations')
      ?.select(`
        *,
        attendance_system_configs (system_name, biometric_types)
      `)
      ?.eq('employee_id', employeeId)
      ?.order('registration_date', { ascending: false });
    
    if (error) throw error;
    
    return data?.map(row => ({
      id: row?.id,
      employeeId: row?.employee_id,
      attendanceSystemId: row?.attendance_system_id,
      systemName: row?.attendance_system_configs?.system_name,
      biometricType: row?.biometric_type,
      biometricId: row?.biometric_id,
      registrationDate: row?.registration_date,
      isActive: row?.is_active,
      createdAt: row?.created_at
    })) || [];
  },

  async deactivateBiometricRegistration(registrationId) {
    const { data, error } = await supabase
      ?.from('employee_biometric_registrations')
      ?.update({
        is_active: false,
        updated_at: new Date()?.toISOString()
      })
      ?.eq('id', registrationId)
      ?.select()
      ?.single();
    
    if (error) throw error;
    
    return {
      id: data?.id,
      isActive: data?.is_active
    };
  },

  // ============================================================================
  // ATTENDANCE RECORDS
  // ============================================================================

  async createAttendanceRecord(record) {
    const { data, error } = await supabase
      ?.from('attendance_records')
      ?.insert({
        employee_id: record?.employeeId,
        attendance_system_id: record?.attendanceSystemId,
        biometric_registration_id: record?.biometricRegistrationId,
        attendance_date: record?.attendanceDate,
        clock_in_time: record?.clockInTime,
        clock_out_time: record?.clockOutTime,
        total_hours: record?.totalHours,
        regular_hours: record?.regularHours,
        overtime_hours: record?.overtimeHours,
        attendance_status: record?.attendanceStatus,
        biometric_verification_method: record?.biometricVerificationMethod,
        location: record?.location,
        notes: record?.notes,
        raw_data: record?.rawData
      })
      ?.select()
      ?.single();
    
    if (error) throw error;
    
    return {
      id: data?.id,
      employeeId: data?.employee_id,
      attendanceDate: data?.attendance_date,
      attendanceStatus: data?.attendance_status
    };
  },

  async getAttendanceRecords(employeeId, startDate, endDate) {
    let query = supabase
      ?.from('attendance_records')
      ?.select('*')
      ?.eq('employee_id', employeeId)
      ?.order('attendance_date', { ascending: false });

    if (startDate) {
      query = query?.gte('attendance_date', startDate);
    }
    if (endDate) {
      query = query?.lte('attendance_date', endDate);
    }

    const { data, error } = await query;
    
    if (error) throw error;
    
    return data?.map(row => ({
      id: row?.id,
      employeeId: row?.employee_id,
      attendanceDate: row?.attendance_date,
      clockInTime: row?.clock_in_time,
      clockOutTime: row?.clock_out_time,
      totalHours: row?.total_hours,
      regularHours: row?.regular_hours,
      overtimeHours: row?.overtime_hours,
      attendanceStatus: row?.attendance_status,
      biometricVerificationMethod: row?.biometric_verification_method,
      location: row?.location,
      notes: row?.notes
    })) || [];
  },

  async updateAttendanceRecord(recordId, updates) {
    const updateData = {
      updated_at: new Date()?.toISOString()
    };

    if (updates?.clockOutTime) updateData.clock_out_time = updates?.clockOutTime;
    if (updates?.totalHours !== undefined) updateData.total_hours = updates?.totalHours;
    if (updates?.regularHours !== undefined) updateData.regular_hours = updates?.regularHours;
    if (updates?.overtimeHours !== undefined) updateData.overtime_hours = updates?.overtimeHours;
    if (updates?.attendanceStatus) updateData.attendance_status = updates?.attendanceStatus;
    if (updates?.notes) updateData.notes = updates?.notes;

    const { data, error } = await supabase
      ?.from('attendance_records')
      ?.update(updateData)
      ?.eq('id', recordId)
      ?.select()
      ?.single();
    
    if (error) throw error;
    
    return {
      id: data?.id,
      attendanceStatus: data?.attendance_status,
      totalHours: data?.total_hours,
      updatedAt: data?.updated_at
    };
  },

  // ============================================================================
  // ATTENDANCE STATISTICS
  // ============================================================================

  async getAttendanceStatistics(employeeId, startDate, endDate) {
    const { data, error } = await supabase
      ?.rpc('get_attendance_statistics', {
        p_employee_id: employeeId,
        p_start_date: startDate,
        p_end_date: endDate
      });
    
    if (error) throw error;
    
    return {
      totalDays: data?.[0]?.total_days || 0,
      presentDays: data?.[0]?.present_days || 0,
      absentDays: data?.[0]?.absent_days || 0,
      lateDays: data?.[0]?.late_days || 0,
      totalHours: data?.[0]?.total_hours || 0,
      regularHours: data?.[0]?.regular_hours || 0,
      overtimeHours: data?.[0]?.overtime_hours || 0
    };
  },

  async getDepartmentAttendanceSummary(department, date) {
    const { data, error } = await supabase
      ?.from('attendance_records')
      ?.select(`
        attendance_status,
        employees!inner (department)
      `)
      ?.eq('employees.department', department)
      ?.eq('attendance_date', date);
    
    if (error) throw error;
    
    const summary = {
      totalEmployees: data?.length || 0,
      present: data?.filter(r => r?.attendance_status === 'present')?.length || 0,
      absent: data?.filter(r => r?.attendance_status === 'absent')?.length || 0,
      late: data?.filter(r => r?.attendance_status === 'late')?.length || 0,
      overtime: data?.filter(r => r?.attendance_status === 'overtime')?.length || 0
    };

    return summary;
  },

  // ============================================================================
  // BIOMETRIC ACCESS AUDIT
  // ============================================================================

  async logBiometricAccess(accessLog) {
    const { error } = await supabase
      ?.from('biometric_access_audit_log')
      ?.insert({
        employee_id: accessLog?.employeeId,
        attendance_system_id: accessLog?.attendanceSystemId,
        biometric_type: accessLog?.biometricType,
        access_type: accessLog?.accessType,
        access_location: accessLog?.accessLocation,
        access_timestamp: accessLog?.accessTimestamp || new Date()?.toISOString(),
        verification_success: accessLog?.verificationSuccess,
        failure_reason: accessLog?.failureReason,
        device_id: accessLog?.deviceId,
        metadata: accessLog?.metadata
      });
    
    if (error) throw error;
  },

  async getBiometricAccessLogs(employeeId, startDate, endDate) {
    let query = supabase
      ?.from('biometric_access_audit_log')
      ?.select('*')
      ?.eq('employee_id', employeeId)
      ?.order('access_timestamp', { ascending: false });

    if (startDate) {
      query = query?.gte('access_timestamp', startDate);
    }
    if (endDate) {
      query = query?.lte('access_timestamp', endDate);
    }

    const { data, error } = await query;
    
    if (error) throw error;
    
    return data?.map(row => ({
      id: row?.id,
      employeeId: row?.employee_id,
      biometricType: row?.biometric_type,
      accessType: row?.access_type,
      accessLocation: row?.access_location,
      accessTimestamp: row?.access_timestamp,
      verificationSuccess: row?.verification_success,
      failureReason: row?.failure_reason,
      deviceId: row?.device_id
    })) || [];
  }
};

export default attendanceIntegrationService;